import React from 'react';
import NumberFormat from 'react-number-format';

import { Box } from '@mui/material';
import NavNeededCountBadge from '@sharedCR/common/NavNeededCountBadge';
import { ACTION_CELL_WIDTH_WITH_BADGES } from '@sharedCR/common/SimpleTable/SimpleTable.constants';
import StatusIcon from '@sharedCR/common/StatusIcon';
import ClickablePhone from 'app/shared/components/ClickablePhone';
import { ENTITY_REFERENCE_TYPE } from 'app/shared/components/TeamChat/TeamChat.entityReferences.utils';
import TeamChatReferenceBadge from 'app/shared/components/TeamChat/TeamChatReferenceBadge';

import { housingTypeOptions } from 'constants/options';
import { StatusEnum } from 'constants/status';

import ActionButton from './ActionButton';
import { actionCellStyles } from './Borrower.styles';
import BorrowerNameCell from './BorrowerNameCell';

const VALUE = -4;

const numberFormatStyle = { color: 'inherit' };

export const getTableConfig = ({
  type,
  pageData,
  entitiesData,
  isCrmEnabled,
  neededCountById = {},
  hasPosDomain = false,
}) => ({
  // key is the name of the property in the data object
  fullName: {
    label: 'Borrower Name',
    width: '18.7%',
    fieldName: 'borrowerFullName',
    // The name cell always shows the separate-application badge; with CRM enabled it
    // also shows the linked contact as a read-only avatar, replacing the standalone
    // CRM Contact column.
    content: rowData => <BorrowerNameCell borrower={rowData} withCrmAvatar={isCrmEnabled} />,
  },
  address: {
    label: 'Current Address',
    width: '25.3%',
  },
  housingType: {
    label: 'Ownership Type',
    width: '11.5%',
    content: ({ housingType }) =>
      housingTypeOptions.find(option => option.value === housingType)?.name,
  },
  currentResidenceOccupiedForMoreThanTwoYears: {
    label: 'Occupied For 2+ Years',
    width: '11.5%',
    content: ({ currentResidenceOccupiedForMoreThanTwoYears }) => (
      <StatusIcon
        status={currentResidenceOccupiedForMoreThanTwoYears ? StatusEnum.SUCCESS : StatusEnum.ERROR}
      />
    ),
    align: 'center',
  },
  isFirstTimeHomeBuyer: {
    label: 'First Time Home Buyer',
    width: '11.7%',
    content: ({ isFirstTimeHomeBuyer }) => (
      <StatusIcon status={isFirstTimeHomeBuyer ? StatusEnum.SUCCESS : StatusEnum.ERROR} />
    ),
    align: 'center',
  },
  ssn: {
    label: 'SSN',
    width: '8%',
    content: ({ ssn }) => (
      <NumberFormat
        data-truncated
        className="truncate"
        displayType="text"
        format="xxx-xx-####"
        style={numberFormatStyle}
        value={ssn?.slice(VALUE)}
      />
    ),
    align: 'right',
  },
  creditScore: {
    label: 'Credit Score',
    width: '8%',
    align: 'right',
  },
  action: {
    label: 'Action',
    width: ACTION_CELL_WIDTH_WITH_BADGES,
    content: rowData => (
      <Box sx={actionCellStyles}>
        <NavNeededCountBadge count={neededCountById[rowData.id] ?? 0} />
        <TeamChatReferenceBadge
          chatId={pageData.chatTargets?.internalChatId}
          entityId={rowData.id}
          entityType={ENTITY_REFERENCE_TYPE.BORROWER}
        />
        <ActionButton
          entitiesData={entitiesData}
          hasPosDomain={hasPosDomain}
          pageData={pageData}
          rowData={rowData}
          type={type}
        />
      </Box>
    ),
    align: 'center',
  },
});

export const getNbeTableConfig = ({ type, pageData, entitiesData }) => ({
  fullName: {
    label: 'Name',
    width: '18.7%',
    fieldName: 'nonBorrowerFullName',
  },
  address: {
    label: 'Address',
    width: '25.3%',
  },
  cellPhoneNumber: {
    label: 'Phone',
    width: '25.5%',
    content: ({ cellPhoneNumber }) => (
      <ClickablePhone applicationId={pageData.applicationId} value={cellPhoneNumber} />
    ),
  },
  emailAddress: {
    label: 'Email',
    width: '25.5%',
  },
  action: {
    label: 'Action',
    width: '5%',
    content: rowData => (
      <ActionButton entitiesData={entitiesData} pageData={pageData} rowData={rowData} type={type} />
    ),
    align: 'center',
  },
});
